import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import { get } from 'lodash';
import { Users, Plus, Trash2, Pencil, X, Bell, ChevronDown, ChevronUp } from 'lucide-react';
import { Button, Spinner, ConfirmationModal, Checkbox, Header } from '@crc-evans/ui-components';
import { message, Input } from 'antd';
import { CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons';
import '../../pages/technical/HeatProfilePage.css';
import './DPRSettingsPipelinesTab.css';
import { useHeatStyleColumnFacets } from '../technical/heatStyleColumnFacets';
import {
  fetchTeamMembersForProject,
  deleteTeamMember,
  addTeamMember,
  getAllNonCognitoUsers,
  addNonCognitoUser,
} from '../../redux/pihEquipmentDetails/action';
import { addRole, fetchRoles } from '../../redux/roles/action';
import { getUsers, getAccess } from '../../services/teamMembers';
import { tabs, normalizeProjectTabLabel } from '../../utils/constants';
import './DPRSettingsTeamsTab.css';
import { ProjectTabTableEmpty } from '../project/ProjectTabTableEmpty';

const getCognitoValue = (key, arr) => {
  if (!Array.isArray(arr)) return '';
  const res = arr.find((obj) => obj.Name === key);
  return res ? res.Value : '';
};

const isWelderRole = (roleName) =>
  String(roleName || '').trim().toLowerCase() === 'welder';

const normEmail = (s) => String(s ?? '').trim().toLowerCase();

const TEAM_FACET_KEYS = ['__nameDisplay', '__emailDisplay', '__roleDisplay', '__dsrLabel', '__commLabel'];

const TEAM_COLUMN_FILTERS_EMPTY = TEAM_FACET_KEYS.reduce((acc, k) => {
  acc[k] = '';
  return acc;
}, {});

function enrichTeamRowForFacets(row) {
  if (!row || typeof row !== 'object') return row;
  const name =
    [row.firstName, row.lastName].filter(Boolean).join(' ') || row.email || '—';
  const emailDisplay = row.isNonCognitoUser ? '—' : row.email || '—';
  return {
    ...row,
    __nameDisplay: name,
    __emailDisplay: emailDisplay,
    __roleDisplay: row.roleName || '—',
    __dsrLabel: row.accessToDsr ? 'Yes' : 'No',
    __commLabel: row.accessToTbt ? 'Yes' : 'No',
  };
}

/** Map Cognito ListUsers API user to dropdown shape */
const mapCognitoListUser = (u) => {
  const fName = getCognitoValue('custom:first_name', u?.Attributes);
  const lName = getCognitoValue('custom:last_name', u?.Attributes);
  const email = getCognitoValue('email', u?.Attributes);
  const displayName =
    fName && lName ? `${fName} ${lName}` : getCognitoValue('name', u?.Attributes) || email;
  if (!displayName && !email) return null;
  return {
    Username: u.Username,
    email,
    firstName: fName || '',
    lastName: lName || '',
    displayName: String(displayName).trim() || email,
    Attributes: u.Attributes,
    isNonCognitoUser: false,
  };
};

const DPRSettingsTeamsTab = ({
  selectedProject,
  projectTeamMembersList,
  isProjectTeamMembersLoading,
  isAddingMemberInProgress,
  isDeletingInProgress,
  roles,
  nonCognitoUsersList,
  isAddingNonCognitoUser,
  projectTeamMembersAccessList,
  userAccessList,
  onFetchTeamMembersForProject,
  onDeleteTeamMember,
  onAddTeamMember,
  onFetchRoles,
  onAddRole,
  onGetAllNonCognitoUsers,
  onAddNonCognitoUser,
}) => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [userOptions, setUserOptions] = useState([]);
  const [defaultUserOptions, setDefaultUserOptions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [isUserSearchLoading, setIsUserSearchLoading] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [accessToDsr, setAccessToDsr] = useState(false);
  const [accessToTbt, setAccessToTbt] = useState(false);
  const [isGuestUser, setIsGuestUser] = useState(false);
  const [isNormalUser, setIsNormalUser] = useState(false);
  const [selectedTabs, setSelectedTabs] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [selectAllWrite, setSelectAllWrite] = useState(false);
  const [permissions, setPermissions] = useState({});
  const [isSelectedUserAdmin, setIsSelectedUserAdmin] = useState(false);
  const [welderId, setWelderId] = useState('');
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);
  const [roleSearch, setRoleSearch] = useState('');
  const [isAddingNewRole, setIsAddingNewRole] = useState(false);
  const [newRoleInput, setNewRoleInput] = useState('');
  const [pendingNewRoleName, setPendingNewRoleName] = useState(null);
  const userDropdownRef = useRef(null);
  const roleDropdownRef = useRef(null);

  const sortedRolesForDropdown = useMemo(() => {
    return [...(roles || [])].sort((a, b) =>
      String(a.name ?? a.roleName ?? '').localeCompare(
        String(b.name ?? b.roleName ?? ''),
        undefined,
        { sensitivity: 'base' }
      )
    );
  }, [roles]);

  const [teamTableSearch, setTeamTableSearch] = useState('');
  const [heatColumnFilters, setHeatColumnFilters] = useState(() => ({ ...TEAM_COLUMN_FILTERS_EMPTY }));
  const [teamSort, setTeamSort] = useState({
    key: '__nameDisplay',
    desc: false,
  });

  const list = Array.isArray(projectTeamMembersList) ? projectTeamMembersList : [];

  // Write access: use userAccessList from Redux, or fallback to localStorage (e.g. when using DPR/ModernLayout where sidebar may not set Redux)
  const isWriteAccess = (() => {
    const groups =
      (userAccessList && userAccessList.length > 0)
        ? userAccessList
        : (() => {
            try {
              return JSON.parse(localStorage.getItem('UserGroup') || '[]');
            } catch (e) {
              return [];
            }
          })();
    if (Array.isArray(groups) && (groups.includes('admin') || groups.includes('super-admin')))
      return true;
    if (projectTeamMembersAccessList?.[0]?.isGuestUser) return false;
    if (
      projectTeamMembersAccessList?.[0]?.isNormalUser &&
      !projectTeamMembersAccessList?.[0]?.tabPermissions?.Team?.write
    )
      return false;
    return true;
  })();

  const teamFacetSource = useMemo(() => {
    const rows = Array.isArray(projectTeamMembersList) ? projectTeamMembersList : [];
    return rows.map((r) => enrichTeamRowForFacets({ ...r }));
  }, [projectTeamMembersList]);

  const handleTeamHeatFilterChange = useCallback((key, value) => {
    setHeatColumnFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const teamFacetRef = useRef([]);
  teamFacetRef.current = teamFacetSource;
  const fetchTeamFacetRows = useCallback(async () => {
    const list = teamFacetRef.current;
    return Array.isArray(list) ? [...list] : [];
  }, []);

  const teamFacetSearchCriteria = useMemo(() => ({}), []);

  const { applyFacetClientFilter, renderTextColumnFilterTrigger } = useHeatStyleColumnFacets({
    enabled: Boolean(selectedProject?.id),
    filterableColumns: TEAM_FACET_KEYS,
    listCount: teamFacetSource.length,
    projectList: teamFacetSource,
    searchCriteria: teamFacetSearchCriteria,
    columnFilters: heatColumnFilters,
    onColumnFilterChange: handleTeamHeatFilterChange,
    fetchFacetRows: fetchTeamFacetRows,
  });

  const lcTeam = (v) => (v == null ? '' : String(v)).trim().toLowerCase();

  const filteredAndFacetedTeam = useMemo(() => {
    let rows = [...teamFacetSource];
    TEAM_FACET_KEYS.forEach((key) => {
      const raw = (heatColumnFilters[key] || '').trim();
      if (!raw) return;
      const q = lcTeam(raw);
      rows = rows.filter((row) => lcTeam(get(row, key)).includes(q));
    });
    return applyFacetClientFilter(rows);
  }, [teamFacetSource, heatColumnFilters, applyFacetClientFilter]);

  const handleTeamSortClick = useCallback((field) => {
    setTeamSort((prev) =>
      prev.key === field ? { ...prev, desc: !prev.desc } : { key: field, desc: false }
    );
  }, []);

  const renderTeamSortCaret = useCallback(
    (field) =>
      teamSort.key === field ? (
        teamSort.desc ? (
          <CaretDownOutlined className="equipment-type-sort-dir-icon" aria-hidden />
        ) : (
          <CaretUpOutlined className="equipment-type-sort-dir-icon" aria-hidden />
        )
      ) : (
        <CaretUpOutlined
          className="equipment-type-sort-dir-icon doc-control-sort-caret-inactive"
          aria-hidden
        />
      ),
    [teamSort.key, teamSort.desc]
  );

  const renderTeamHeader = useCallback(
    (labelNode, dataIndex, sortField, { filterable = true, headerAlign = 'center', searchWidth = 120, showSearch = true } = {}) => {
      if (!filterable) {
        return (
          <div className="column-header-with-filter column-header-with-filter--no-search">
            <div
              className={`column-header-text-wrapper dpr-pipelines-heat-title-row${
                headerAlign === 'left' ? ' dpr-pipelines-heat-title-row--left' : ''
              }`}
            >
              <span className="dpr-pipelines-heat-label">{labelNode}</span>
            </div>
          </div>
        );
      }
      return (
        <div
          className={`column-header-with-filter${
            showSearch ? '' : ' column-header-with-filter--no-search'
          }`}
        >
          <div
            className={`column-header-text-wrapper spares-column-header-sortable dpr-pipelines-heat-title-row${
              headerAlign === 'left' ? ' dpr-pipelines-heat-title-row--left' : ''
            }`}
          >
            <span
              className="dpr-pipelines-heat-label dpr-pipelines-heat-label--sortable"
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                handleTeamSortClick(sortField);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleTeamSortClick(sortField);
                }
              }}
            >
              {labelNode}
            </span>
            <span
              className="custom-sort-icon"
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleTeamSortClick(sortField);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleTeamSortClick(sortField);
                }
              }}
            >
              {renderTeamSortCaret(sortField)}
            </span>
            {renderTextColumnFilterTrigger(dataIndex)}
          </div>
          {showSearch ? (
            <div className="header-filter-input user-mgmt-header-search">
              <div className="heat-profile-inline-field-shell heat-profile-inline-field-shell--sm heat-profile-header-inline-shell">
                <Input
                  placeholder="Search"
                  value={heatColumnFilters[dataIndex] ?? ''}
                  onChange={(e) => handleTeamHeatFilterChange(dataIndex, e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  size="small"
                  style={{ width: searchWidth }}
                />
              </div>
            </div>
          ) : null}
        </div>
      );
    },
    [
      handleTeamHeatFilterChange,
      handleTeamSortClick,
      heatColumnFilters,
      renderTeamSortCaret,
      renderTextColumnFilterTrigger,
    ]
  );

  const sortedTeamDisplayList = useMemo(() => {
    const rows = [...filteredAndFacetedTeam];
    const cmpStr = (a, b, key) =>
      String(get(a, key) ?? '')
        .trim()
        .toLowerCase()
        .localeCompare(String(get(b, key) ?? '').trim().toLowerCase(), undefined, {
          sensitivity: 'base',
          numeric: true,
        });
    const tieBreak = (a, b) => {
      const idA = a?.id != null ? Number(a.id) : NaN;
      const idB = b?.id != null ? Number(b.id) : NaN;
      if (Number.isFinite(idA) && Number.isFinite(idB) && idA !== idB) return idA < idB ? -1 : 1;
      return String(a?.id ?? '').localeCompare(String(b?.id ?? ''), undefined, { numeric: true });
    };
    const { key: sortKey, desc: sortDesc } = teamSort;
    rows.sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case '__nameDisplay':
        case '__emailDisplay':
        case '__roleDisplay':
        case '__dsrLabel':
        case '__commLabel':
          cmp = cmpStr(a, b, sortKey);
          break;
        default:
          cmp = 0;
      }
      if (cmp === 0) cmp = tieBreak(a, b);
      return sortDesc ? -cmp : cmp;
    });
    return rows;
  }, [filteredAndFacetedTeam, teamSort]);

  const displayTeamList = sortedTeamDisplayList;

  useEffect(() => {
    if (selectedProject?.id) {
      setHeatColumnFilters({ ...TEAM_COLUMN_FILTERS_EMPTY });
      setTeamSort({ key: '__nameDisplay', desc: false });
      onFetchTeamMembersForProject(selectedProject);
    }
  }, [selectedProject?.id, onFetchTeamMembersForProject]);

  useEffect(() => {
    onFetchRoles();
    onGetAllNonCognitoUsers?.();
  }, [onFetchRoles, onGetAllNonCognitoUsers]);

  useEffect(() => {
    if (!pendingNewRoleName) return;
    const match = sortedRolesForDropdown.find(
      (r) =>
        String(r.name ?? r.roleName ?? '').toLowerCase() === pendingNewRoleName.toLowerCase()
    );
    if (match) {
      setSelectedRole(match);
      setPendingNewRoleName(null);
      setRoleDropdownOpen(false);
      if (!isWelderRole(match?.name ?? match?.roleName)) setWelderId('');
    }
  }, [pendingNewRoleName, sortedRolesForDropdown]);

  const handleAddNewRoleSubmit = () => {
    const trimmed = newRoleInput.trim();
    if (!trimmed) return;

    const exists = sortedRolesForDropdown.some(
      (r) => String(r.name ?? r.roleName ?? '').toLowerCase() === trimmed.toLowerCase()
    );
    if (exists) {
      message.warning('Role already exists!');
      return;
    }

    setPendingNewRoleName(trimmed);
    setSelectedRole({ name: trimmed, roleName: trimmed });
    onAddRole?.({ name: trimmed });
    setNewRoleInput('');
    setIsAddingNewRole(false);
  };

  useEffect(() => {
    if (!isAddingMemberInProgress && isSaving) {
      setIsSaving(false);
      setIsAddModalOpen(false);
      setEditRecord(null);
      resetModalFields();
    }
  }, [isAddingMemberInProgress, isSaving]);

  const prevDeletingInProgress = useRef(false);
  useEffect(() => {
    const wasDeleting = prevDeletingInProgress.current;
    prevDeletingInProgress.current = isDeletingInProgress;
    if (wasDeleting && !isDeletingInProgress && memberToDelete) {
      setIsDeleteModalOpen(false);
      setMemberToDelete(null);
    }
  }, [isDeletingInProgress, memberToDelete]);

  // Load users when Add modal opens so dropdown has options
  useEffect(() => {
    if (isAddModalOpen && !editRecord) {
      setUserDropdownOpen(false);
      setUserOptions([]);
      setDefaultUserOptions([]);
      setIsUserSearchLoading(true);
      getUsers('')
        .then((res) => {
          const users = res?.users || [];
          const mapped = users
            .map((u) => {
              const fName = getCognitoValue('custom:first_name', u?.Attributes);
              const lName = getCognitoValue('custom:last_name', u?.Attributes);
              const email = getCognitoValue('email', u?.Attributes);
              const displayName =
                fName && lName ? `${fName} ${lName}` : getCognitoValue('name', u?.Attributes) || email;
              if (!displayName && !email) return null;
              return {
                Username: u.Username,
                email,
                firstName: fName || '',
                lastName: lName || '',
                displayName: displayName.trim() || email,
                Attributes: u.Attributes,
                isNonCognitoUser: false,
              };
            })
            .filter(Boolean);
          setDefaultUserOptions(mapped);
          setUserOptions(mapped);
        })
        .catch(() => {
          setDefaultUserOptions([]);
          setUserOptions([]);
        })
        .finally(() => setIsUserSearchLoading(false));
    }
  }, [isAddModalOpen, editRecord]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(e.target)) setUserDropdownOpen(false);
      if (roleDropdownRef.current && !roleDropdownRef.current.contains(e.target)) setRoleDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchAccess = async () => {
      if (selectedUser?.Username) {
        try {
          const response = await getAccess(selectedUser.Username);
          const groups = Array.isArray(response) ? response.map((x) => x.GroupName) : [];
          setIsSelectedUserAdmin(
            groups.includes('admin') || groups.includes('super-admin')
          );
          if (isSelectedUserAdmin) {
            setIsGuestUser(false);
            setIsNormalUser(false);
            setSelectedTabs([]);
            setPermissions({});
            setSelectAll(false);
          }
        } catch (e) {
          setIsSelectedUserAdmin(false);
        }
      } else {
        setIsSelectedUserAdmin(false);
      }
    };
    fetchAccess();
  }, [selectedUser?.Username]);

  const cognitoUsersList = (() => {
    let users = [];
    if (userOptions.length > 0) users = [...userOptions];
    if (Array.isArray(nonCognitoUsersList) && nonCognitoUsersList.length > 0) {
      const non = nonCognitoUsersList
        .map((u) => {
          const displayName = `${u?.firstName || ''} ${u?.lastName || ''}`.trim();
          if (!displayName) return null;
          return {
            ...u,
            displayName,
            Username: u.cognitoUserId,
            isNonCognitoUser: true,
            email: u.email || '',
            firstName: u.firstName || '',
            lastName: u.lastName || '',
          };
        })
        .filter(Boolean);
      users = [...users, ...non];
    }
    return users;
  })();

  function resetModalFields() {
    setSelectedUser(null);
    setSelectedRole(null);
    setUserSearch('');
    setRoleSearch('');
    setRoleDropdownOpen(false);
    setIsAddingNewRole(false);
    setNewRoleInput('');
    setPendingNewRoleName(null);
    setUserOptions([]);
    setDefaultUserOptions([]);
    setAccessToDsr(false);
    setAccessToTbt(false);
    setIsGuestUser(false);
    setIsNormalUser(false);
    setSelectedTabs([]);
    setSelectAll(false);
    setSelectAllWrite(false);
    setPermissions({});
    setWelderId('');
  }

  const handleAddClick = () => {
    setEditRecord(null);
    resetModalFields();
    setIsGuestUser(true);
    setSelectAll(false);
    setSelectedTabs([]);
    setPermissions({});
    setIsAddModalOpen(true);
  };

  const handleUserSearch = async (value) => {
    setUserSearch(value);
    if (!value || value.trim().length < 2) {
      setUserOptions(defaultUserOptions);
      return;
    }
    setIsUserSearchLoading(true);
    try {
      const res = await getUsers('', value);
      const users = res?.users || [];
      const mapped = users
        .map((u) => {
          const fName = getCognitoValue('custom:first_name', u?.Attributes);
          const lName = getCognitoValue('custom:last_name', u?.Attributes);
          const email = getCognitoValue('email', u?.Attributes);
          const displayName =
            fName && lName ? `${fName} ${lName}` : getCognitoValue('name', u?.Attributes) || email;
          if (!displayName && !email) return null;
          return {
            Username: u.Username,
            email,
            firstName: fName || '',
            lastName: lName || '',
            displayName: displayName.trim() || email,
            Attributes: u.Attributes,
            isNonCognitoUser: false,
          };
        })
        .filter(Boolean);
      setUserOptions(mapped);
    } catch (e) {
      setUserOptions([]);
    } finally {
      setIsUserSearchLoading(false);
    }
  };

  const handleEditClick = async (record) => {
    if (!isWriteAccess) return;
    setEditRecord(record);
    setAccessToDsr(!!record.accessToDsr);
    setAccessToTbt(!!record.accessToTbt);
    setIsGuestUser(!!record.isGuestUser);
    setIsNormalUser(!!record.isNormalUser);
    setSelectedTabs(
      Array.isArray(record.selectedTabs)
        ? record.selectedTabs.map(normalizeProjectTabLabel)
        : []
    );
    const rawPermissions =
      typeof record.tabPermissions === 'object' ? record.tabPermissions : {};
    const permissionsNormalized = { ...rawPermissions };
    if (rawPermissions.Fabrication && !permissionsNormalized.Production) {
      permissionsNormalized.Production = rawPermissions.Fabrication;
    }
    setPermissions(permissionsNormalized);
    setSelectAll(Array.isArray(record.selectedTabs) && record.selectedTabs.length === tabs.length);
    const editTabs = Array.isArray(record.selectedTabs)
      ? record.selectedTabs.map(normalizeProjectTabLabel)
      : [];
    setSelectAllWrite(
      editTabs.length > 0 &&
        editTabs.every((tab) => !!permissionsNormalized[tab]?.write)
    );
    setWelderId(record.welderId || '');

    const recordEmailNorm = normEmail(record.email);
    const recordUsername = String(record.cognitoUserId || record.username || '').trim();

    // Dropdown list is usually empty in edit mode (we don't prefetch all Cognito users when editRecord is set).
    let userData = cognitoUsersList.find(
      (c) =>
        (recordEmailNorm && normEmail(c.email) === recordEmailNorm) ||
        (recordUsername && String(c.Username || '') === recordUsername),
    );

    const pickFromUsersList = (users) => {
      if (!Array.isArray(users) || users.length === 0) return null;
      const byId = recordUsername
        ? users.find((u) => String(u.Username) === recordUsername)
        : null;
      if (byId) return mapCognitoListUser(byId);
      const byEmail = recordEmailNorm
        ? users.find((u) => normEmail(getCognitoValue('email', u?.Attributes)) === recordEmailNorm)
        : null;
      if (byEmail) return mapCognitoListUser(byEmail);
      return mapCognitoListUser(users[0]);
    };

    if (!userData && record.email) {
      try {
        const res = await getUsers('', record.email);
        userData = pickFromUsersList(res?.users || []);
      } catch (e) {
        userData = null;
      }
    }

    if (!userData && recordUsername && recordUsername !== record.email) {
      try {
        const res = await getUsers('', recordUsername);
        userData = pickFromUsersList(res?.users || []);
      } catch (e) {
        userData = null;
      }
    }

    if (!userData && record.isNonCognitoUser) {
      userData = {
        Username: record.cognitoUserId,
        email: record.email || '',
        firstName: record.firstName || '',
        lastName: record.lastName || '',
        displayName: [record.firstName, record.lastName].filter(Boolean).join(' ') || '—',
        isNonCognitoUser: true,
      };
    }

    // Always show name/email from the row when Cognito lookup misses (case mismatch, stale pool, etc.)
    if (!userData) {
      const displayName =
        [record.firstName, record.lastName].filter(Boolean).join(' ') ||
        record.name ||
        record.fullName ||
        record.email ||
        'Team member';
      userData = {
        Username: record.cognitoUserId || record.userId || record.username || record.email || '',
        email: record.email || '',
        firstName: record.firstName || '',
        lastName: record.lastName || '',
        displayName,
        isNonCognitoUser: !!record.isNonCognitoUser,
      };
    }

    setSelectedUser(userData);
    setUserSearch(userData.displayName || '');
    const roleData = (roles || []).find((r) => (r.name || r.roleName) === record.roleName);
    setSelectedRole(roleData || null);
    setIsAddModalOpen(true);
  };

  const handleNotificationClick = (record) => {
    if (!isWriteAccess) return;
    message.info('Send notification – use main project Team tab if needed.');
  };

  const buildPayload = (resolvedRoleId) => {
    const roleId = resolvedRoleId ?? selectedRole?.id ?? selectedRole?.roleId;
    const email = selectedUser?.Attributes
      ? getCognitoValue('email', selectedUser.Attributes)
      : selectedUser?.email || '';
    const firstName = selectedUser?.Attributes
      ? getCognitoValue('custom:first_name', selectedUser.Attributes) ||
        getCognitoValue('name', selectedUser.Attributes)
      : selectedUser?.firstName || '';
    const lastName = selectedUser?.Attributes
      ? getCognitoValue('custom:last_name', selectedUser.Attributes)
      : selectedUser?.lastName || '';

    return {
      cognitoUserId: selectedUser.Username,
      email,
      firstName,
      lastName,
      projectId: selectedProject.id,
      roleId: Number(roleId),
      accessToDsr,
      accessToTbt: accessToTbt ? 1 : 0,
      isGuestUser,
      isNormalUser,
      selectedTabs,
      tabPermissions: isNormalUser ? permissions : {},
      isNonCognitoUser: !!selectedUser?.isNonCognitoUser,
      ...(isWelderRole(selectedRole?.name ?? selectedRole?.roleName) ? { welderId: welderId || '' } : {}),
    };
  };

  const handleAddSubmit = () => {
    if (!selectedUser || !selectedRole || !selectedProject) {
      message.warning('Please select a user and a job title.');
      return;
    }
    let roleId = selectedRole.id ?? selectedRole.roleId;
    if (!roleId) {
      const roleLabel = String(selectedRole.name ?? selectedRole.roleName ?? '').trim();
      const match = sortedRolesForDropdown.find(
        (r) =>
          String(r.name ?? r.roleName ?? '').trim().toLowerCase() === roleLabel.toLowerCase()
      );
      roleId = match?.id ?? match?.roleId;
    }
    if (!roleId) {
      message.warning('Job title is still saving. Please wait a moment and try again.');
      onFetchRoles?.();
      return;
    }
    setIsSaving(true);
    onAddTeamMember({
      addMemberPayload: buildPayload(roleId),
      selectedProject,
    });
  };

  const handleDeleteClick = (record) => {
    setMemberToDelete(record);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (!memberToDelete || !selectedProject) return;
    onDeleteTeamMember({
      data: {
        teamMemberData: memberToDelete,
        selectedProject,
      },
    });
    // Modal closes when isDeletingInProgress becomes false (useEffect)
  };

  const handleGuestUserChange = (checked) => {
    setIsGuestUser(checked);
    if (checked) {
      setIsNormalUser(false);
      setSelectAll(false);
      setSelectAllWrite(false);
      setSelectedTabs([]);
      setPermissions({});
    }
  };

  const handleNormalUserChange = (checked) => {
    setIsNormalUser(checked);
    if (checked) {
      setIsGuestUser(false);
      setSelectAll(true);
      setSelectAllWrite(false);
      const allTabs = tabs.map((t) => t.label);
      setSelectedTabs(allTabs);
      const updatedPermissions = {};
      allTabs.forEach((tab) => {
        updatedPermissions[tab] = { read: true, write: false };
      });
      setPermissions(updatedPermissions);
    } else {
      handleGuestUserChange(true);
    }
  };

  const handleSelectAllChange = (checked) => {
    setSelectAll(checked);
    if (checked) {
      const allTabs = tabs.map((t) => t.label);
      setSelectedTabs(allTabs);
      const updatedPermissions = {};
      allTabs.forEach((tab) => {
        updatedPermissions[tab] = { read: true, write: !!selectAllWrite };
      });
      setPermissions(updatedPermissions);
    } else {
      setSelectedTabs([]);
      setPermissions({});
      setSelectAllWrite(false);
    }
  };

  const handleSelectAllWriteChange = (checked) => {
    setSelectAllWrite(checked);
    let targets = selectedTabs;
    if (checked && targets.length === 0) {
      targets = tabs.map((t) => t.label);
      setSelectedTabs(targets);
      setSelectAll(true);
    }
    if (targets.length === 0) return;
    setPermissions((prev) => {
      const next = { ...prev };
      targets.forEach((tab) => {
        next[tab] = {
          read: next[tab]?.read !== false,
          write: checked,
        };
      });
      return next;
    });
  };

  const handleTabChange = (checkedValues) => {
    setSelectedTabs(checkedValues);
    setSelectAll(checkedValues.length === tabs.length);
    const updatedPermissions = { ...permissions };
    checkedValues.forEach((tabLabel) => {
      if (!updatedPermissions[tabLabel]) {
        updatedPermissions[tabLabel] = { read: true, write: !!selectAllWrite };
      }
    });
    Object.keys(updatedPermissions).forEach((tabLabel) => {
      if (!checkedValues.includes(tabLabel)) delete updatedPermissions[tabLabel];
    });
    setPermissions(updatedPermissions);
    setSelectAllWrite(
      checkedValues.length > 0 &&
        checkedValues.every((tab) => !!updatedPermissions[tab]?.write)
    );
  };

  const handlePermissionChange = (tabLabel, permissionType, isChecked) => {
    setPermissions((prev) => {
      const next = {
        ...prev,
        [tabLabel]: { ...(prev[tabLabel] || {}), [permissionType]: isChecked },
      };
      if (permissionType === 'write') {
        const tabsWithPerms = selectedTabs.filter((t) => next[t]);
        setSelectAllWrite(
          tabsWithPerms.length > 0 && tabsWithPerms.every((t) => !!next[t]?.write)
        );
      }
      return next;
    });
  };

  const handleCloseAddModal = () => {
    if (!isSaving) {
      setIsAddModalOpen(false);
      setEditRecord(null);
      resetModalFields();
    }
  };

  return (
    <div className="dpr-settings-teams-tab">
      {isProjectTeamMembersLoading && (
        <div className="dpr-settings-teams-tab-loading">
          <Spinner variant="inline" size="medium" />
        </div>
      )}

      <div className="dpr-settings-teams-tab-card">
        <div className="dpr-settings-teams-tab-header">
          <div className="dpr-settings-teams-tab-header-left">
            <div className="dpr-settings-teams-tab-icon-wrap">
              <Users size={24} />
            </div>
            <div>
              <div className="dpr-settings-teams-tab-title-row">
                <h3 className="dpr-settings-teams-tab-title">Teams</h3>
                <span className="dpr-settings-teams-tab-summary">
                  Total: {displayTeamList.length}{' '}
                  {displayTeamList.length === 1 ? 'member' : 'members'}
                </span>
              </div>
              <p className="dpr-settings-teams-tab-description">
                Manage team members, roles, and contact information.
              </p>
            </div>
          </div>
          <Button
            label="Add Team Member"
            variant="primary"
            size="small"
            icon={Plus}
            onClick={handleAddClick}
            disabled={!isWriteAccess}
          />
        </div>

        <div className="dpr-settings-teams-tab-table-shell">
          <div className="dpr-settings-teams-tab-table-wrap dpr-settings-pipelines-table-wrap">
            <div className="dpr-settings-teams-tab-thead">
              <div className="dpr-settings-teams-tab-header-row dpr-settings-teams-tab-header-row--heat">
                <div className="dpr-settings-teams-tab-cell dpr-settings-teams-tab-cell-index dpr-settings-teams-tab-cell-header-compact">
                  {renderTeamHeader('#', null, null, { filterable: false, headerAlign: 'center' })}
                </div>
                <div className="dpr-settings-teams-tab-cell dpr-settings-teams-tab-cell-name">
                  {renderTeamHeader('NAME', '__nameDisplay', '__nameDisplay', { headerAlign: 'left' })}
                </div>
                <div className="dpr-settings-teams-tab-cell dpr-settings-teams-tab-cell-email">
                  {renderTeamHeader('EMAIL', '__emailDisplay', '__emailDisplay', { headerAlign: 'left' })}
                </div>
                <div className="dpr-settings-teams-tab-cell dpr-settings-teams-tab-cell-role">
                  {renderTeamHeader('JOB TITLE', '__roleDisplay', '__roleDisplay', { headerAlign: 'left' })}
                </div>
                <div className="dpr-settings-teams-tab-cell dpr-settings-teams-tab-cell-dsr dpr-settings-teams-tab-cell-header-compact">
                  {renderTeamHeader('DSR', '__dsrLabel', '__dsrLabel', { showSearch: false })}
                </div>
                <div className="dpr-settings-teams-tab-cell dpr-settings-teams-tab-cell-comm dpr-settings-teams-tab-cell-header-compact">
                  {renderTeamHeader('COMMUNICATIONS', '__commLabel', '__commLabel', { showSearch: false })}
                </div>
                <div className="dpr-settings-teams-tab-cell dpr-settings-teams-tab-cell-actions dpr-settings-teams-tab-cell-header-compact">
                  {renderTeamHeader('ACTIONS', null, null, {
                    filterable: false,
                    headerAlign: 'center',
                  })}
                </div>
              </div>
            </div>

            {list.length === 0 ? (
              <div className="dpr-settings-teams-tab-empty">
                <ProjectTabTableEmpty />
              </div>
            ) : displayTeamList.length === 0 ? (
              <div className="dpr-settings-teams-tab-empty">
                <ProjectTabTableEmpty />
              </div>
            ) : (
              <div className="dpr-settings-teams-tab-rows">
                {displayTeamList.map((row, index) => (
                  <div key={row.id} className="dpr-settings-teams-tab-data-row">
                  <div className="dpr-settings-teams-tab-cell dpr-settings-teams-tab-cell-index">
                    {index + 1}
                  </div>
                  <div className="dpr-settings-teams-tab-cell dpr-settings-teams-tab-cell-name">
                    {row.firstName || row.lastName
                      ? [row.firstName, row.lastName].filter(Boolean).join(' ')
                      : row.email || '—'}
                  </div>
                  <div className="dpr-settings-teams-tab-cell dpr-settings-teams-tab-cell-email">
                    {!row.isNonCognitoUser ? row.email || '—' : '—'}
                  </div>
                  <div className="dpr-settings-teams-tab-cell dpr-settings-teams-tab-cell-role">
                    {row.roleName || '—'}
                  </div>
                  <div className="dpr-settings-teams-tab-cell dpr-settings-teams-tab-cell-dsr dpr-settings-teams-tab-cell-check">
                    <Checkbox checked={!!row.accessToDsr} disabled borderColor="#00C9A7" onChange={() => {}} />
                  </div>
                  <div className="dpr-settings-teams-tab-cell dpr-settings-teams-tab-cell-comm dpr-settings-teams-tab-cell-check">
                    <Checkbox checked={!!row.accessToTbt} disabled borderColor="#00C9A7" onChange={() => {}} />
                  </div>
                  <div className="dpr-settings-teams-tab-cell dpr-settings-teams-tab-cell-actions">
                    <button
                      type="button"
                      className="dpr-settings-teams-tab-action-btn"
                      onClick={() => handleEditClick(row)}
                      title="Update"
                      aria-label="Update"
                      disabled={!isWriteAccess}
                      style={{ opacity: isWriteAccess ? 1 : 0.7 }}
                    >
                      <Pencil size={18} />
                    </button>
                    <button
                      type="button"
                      className="dpr-settings-teams-tab-action-btn"
                      onClick={() => handleNotificationClick(row)}
                      title="Send Notification"
                      aria-label="Send Notification"
                      disabled={!isWriteAccess}
                      style={{ opacity: isWriteAccess ? 1 : 0.7 }}
                    >
                      <Bell size={18} />
                    </button>
                    <button
                      type="button"
                      className="dpr-settings-teams-tab-action-btn dpr-settings-teams-tab-action-delete"
                      onClick={() => handleDeleteClick(row)}
                      title="Delete Member"
                      aria-label="Delete"
                      disabled={!isWriteAccess}
                      style={{ opacity: isWriteAccess ? 1 : 0.7 }}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add / Edit Team Member modal */}
      {isAddModalOpen && (
        <div className="dpr-settings-teams-tab-modal-backdrop" onClick={handleCloseAddModal}>
          <div className="dpr-settings-teams-tab-modal dpr-settings-teams-tab-modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="dpr-settings-teams-tab-modal-head">
              <h3 className="dpr-settings-teams-tab-modal-title">
                {editRecord ? 'Update Member To Project' : 'Add Member To Project'}
              </h3>
              <button
                type="button"
                className="dpr-settings-teams-tab-modal-close"
                onClick={handleCloseAddModal}
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
            <div className="dpr-settings-teams-tab-modal-body">
              <div className="dpr-settings-teams-tab-modal-field">
                <label>User</label>
                <div ref={userDropdownRef} className="dpr-settings-teams-tab-user-dropdown">
                  <div
                    className="dpr-settings-teams-tab-select-trigger"
                    onClick={() => !editRecord && setUserDropdownOpen(!userDropdownOpen)}
                    style={{ cursor: editRecord ? 'default' : 'pointer', opacity: editRecord ? 0.8 : 1 }}
                  >
                    <span className="dpr-settings-teams-tab-select-value">
                      {selectedUser
                        ? `${selectedUser.displayName}${selectedUser.email ? ` (${selectedUser.email})` : ''}`
                        : isUserSearchLoading
                        ? 'Loading...'
                        : 'Search by user name or email'}
                    </span>
                    {!editRecord && (
                      <span className="dpr-settings-teams-tab-select-chevron">
                        {userDropdownOpen ? <ChevronUp size={18} color="#6B7280" /> : <ChevronDown size={18} color="#6B7280" />}
                      </span>
                    )}
                  </div>
                  {userDropdownOpen && !editRecord && (
                    <div
                      className="dpr-settings-teams-tab-user-list-wrap"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="text"
                        placeholder="Type to filter..."
                        value={userSearch}
                        onChange={(e) => handleUserSearch(e.target.value)}
                        autoFocus
                        className="dpr-settings-teams-tab-user-filter"
                        onMouseDown={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        onFocus={() => setUserDropdownOpen(true)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="dpr-settings-teams-tab-user-list">
                        {(userSearch.trim()
                          ? userOptions.filter(
                              (u) =>
                                (u.displayName || '').toLowerCase().includes(userSearch.toLowerCase()) ||
                                (u.email || '').toLowerCase().includes(userSearch.toLowerCase())
                            )
                          : userOptions
                        )
                          .slice(0, 50)
                          .map((u) => (
                            <div
                              key={u.Username}
                              className="dpr-settings-teams-tab-user-opt"
                              onClick={() => {
                                setSelectedUser(u);
                                setUserSearch('');
                                setUserDropdownOpen(false);
                              }}
                            >
                              {u.displayName} {u.email ? `(${u.email})` : ''}
                            </div>
                          ))}
                        {userOptions.length === 0 && !isUserSearchLoading && (
                          <div className="dpr-settings-teams-tab-user-opt" style={{ color: '#94A3B8', cursor: 'default' }}>
                            No users found
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="dpr-settings-teams-tab-modal-field">
                <label>Job title</label>
                <div ref={roleDropdownRef} style={{ position: 'relative', width: '100%' }}>
                  <div
                    className="dpr-settings-teams-tab-select-trigger"
                    onClick={() => setRoleDropdownOpen(!roleDropdownOpen)}
                    style={{
                      border: roleDropdownOpen ? '2px solid #3B82F6' : '1px solid #E2E8F0',
                    }}
                  >
                    <span className="dpr-settings-teams-tab-select-value">
                      {selectedRole ? (selectedRole.name ?? selectedRole.roleName ?? '') : 'Select job title'}
                    </span>
                    <span className="dpr-settings-teams-tab-select-chevron">
                      {roleDropdownOpen ? <ChevronUp size={18} color="#6B7280" /> : <ChevronDown size={18} color="#6B7280" />}
                    </span>
                  </div>
                  {roleDropdownOpen && (
                    <div
                      className="dpr-settings-teams-tab-user-list-wrap"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="text"
                        placeholder="Type to filter job titles..."
                        value={roleSearch}
                        onChange={(e) => setRoleSearch(e.target.value)}
                        autoFocus
                        className="dpr-settings-teams-tab-user-filter"
                        onMouseDown={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        onFocus={() => setRoleDropdownOpen(true)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="dpr-settings-teams-tab-role-list">
                        {(roleSearch.trim()
                          ? sortedRolesForDropdown.filter((r) =>
                              String(r.name ?? r.roleName ?? '')
                                .toLowerCase()
                                .includes(roleSearch.trim().toLowerCase())
                            )
                          : sortedRolesForDropdown
                        ).map((r, index) => (
                          <div
                            key={
                              r.id ?? r.roleId
                                ? `role-id-${r.id ?? r.roleId}`
                                : `role-name-${String(r.name ?? r.roleName ?? index).toLowerCase()}`
                            }
                            className="dpr-settings-teams-tab-role-opt"
                            onClick={() => {
                              setSelectedRole(r);
                              setRoleSearch('');
                              setRoleDropdownOpen(false);
                              if (!isWelderRole(r?.name ?? r?.roleName)) setWelderId('');
                            }}
                          >
                            {r.name ?? r.roleName ?? ''}
                          </div>
                        ))}
                        {sortedRolesForDropdown.length === 0 && (
                          <div style={{ padding: '8px 12px', color: '#94A3B8', fontSize: '14px' }}>No roles loaded</div>
                        )}
                        {sortedRolesForDropdown.length > 0 &&
                          roleSearch.trim() &&
                          sortedRolesForDropdown.filter((r) =>
                            String(r.name ?? r.roleName ?? '')
                              .toLowerCase()
                              .includes(roleSearch.trim().toLowerCase())
                          ).length === 0 && (
                          <div style={{ padding: '8px 12px', color: '#94A3B8', fontSize: '14px' }}>No matching job titles</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {isWelderRole(selectedRole?.name ?? selectedRole?.roleName) && (
                <div className="dpr-settings-teams-tab-modal-field">
                  <label>Welder ID</label>
                  <Input
                    value={welderId}
                    onChange={(e) => setWelderId(e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px' }}
                    placeholder="Welder ID"
                    onKeyDown={(e) => e.stopPropagation()}
                  />
                </div>
              )}

              <div className="dpr-settings-teams-tab-modal-checkbox-section">
                <div className="dpr-settings-teams-tab-modal-checkbox-row">
                  <Checkbox
                    label="To be listed in DSR"
                    checked={accessToDsr}
                    onChange={(checked) => setAccessToDsr(!!checked)}
                    borderColor="#00C9A7"
                  />
                  <Checkbox
                    label="To be listed in Communications"
                    checked={!!accessToTbt}
                    onChange={(checked) => setAccessToTbt(checked ? 1 : 0)}
                    borderColor="#00C9A7"
                  />
                </div>

                <div className="dpr-settings-teams-tab-modal-section-divider" />

                <div className="dpr-settings-teams-tab-modal-checkbox-row">
                  <Checkbox
                    label="Guest User"
                    checked={isGuestUser}
                    onChange={(checked) => handleGuestUserChange(!!checked)}
                    disabled={isSelectedUserAdmin}
                    borderColor="#00C9A7"
                  />
                  <Checkbox
                    label="Normal User"
                    checked={isNormalUser}
                    onChange={(checked) => handleNormalUserChange(!!checked)}
                    disabled={isSelectedUserAdmin}
                    borderColor="#00C9A7"
                  />
                </div>

                {!isSelectedUserAdmin && (
                  <>
                    <div className="dpr-settings-teams-tab-modal-checkbox-row dpr-settings-teams-tab-select-all-row">
                      <Checkbox
                        label="Select ALL"
                        checked={selectAll}
                        onChange={(checked) => handleSelectAllChange(!!checked)}
                        borderColor="#00C9A7"
                      />
                      {isNormalUser && (
                        <Checkbox
                          label="Select WRITE"
                          checked={selectAllWrite}
                          onChange={(checked) => handleSelectAllWriteChange(!!checked)}
                          borderColor="#00C9A7"
                        />
                      )}
                    </div>
                    <div className="dpr-settings-teams-tab-tabs-grid">
                      {tabs.map((tab) => (
                        <div key={tab.label} className="dpr-settings-teams-tab-tab-row">
                          <Checkbox
                            label={tab.label}
                            checked={selectedTabs.includes(tab.label)}
                            onChange={(checked) => {
                              const next = checked
                                ? [...selectedTabs, tab.label]
                                : selectedTabs.filter((t) => t !== tab.label);
                              handleTabChange(next);
                            }}
                            borderColor="#00C9A7"
                          />
                          {isNormalUser && selectedTabs.includes(tab.label) && permissions[tab.label] && (
                            <div className="dpr-settings-teams-tab-permissions">
                              <Checkbox
                                label="Read"
                                checked={!!permissions[tab.label]?.read}
                                onChange={(checked) => handlePermissionChange(tab.label, 'read', !!checked)}
                                borderColor="#00C9A7"
                              />
                              <Checkbox
                                label="Write"
                                checked={!!permissions[tab.label]?.write}
                                onChange={(checked) => handlePermissionChange(tab.label, 'write', !!checked)}
                                borderColor="#00C9A7"
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {isSelectedUserAdmin && (
                <div className="dpr-settings-teams-tab-warn-box">
                  The selected user has an Admin role and will have full access to the project.
                </div>
              )}

              {!isSelectedUserAdmin && isGuestUser && selectedTabs.length > 0 && (
                <div className="dpr-settings-teams-tab-warn-box">
                  Guest users are granted view-only access.
                </div>
              )}
            </div>
            <div className="dpr-settings-teams-tab-modal-foot">
              <Button label="Cancel" variant="tertiary" onClick={handleCloseAddModal} size="medium" disabled={isSaving} />
              <Button
                label={
                  isSaving
                    ? editRecord
                      ? 'Updating…'
                      : 'Adding…'
                    : editRecord
                    ? 'Update'
                    : 'Add'
                }
                variant="primary"
                onClick={handleAddSubmit}
                disabled={isSaving || !selectedUser || !selectedRole}
                size="medium"
              />
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        title="Delete Team Member"
        message={`Are you sure you want to delete ${memberToDelete ? [memberToDelete.firstName, memberToDelete.lastName].filter(Boolean).join(' ') || memberToDelete.email : 'this member'}?`}
        confirmText={isDeletingInProgress ? 'Deleting…' : 'Delete'}
        cancelText="Cancel"
        isOpen={isDeleteModalOpen}
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setIsDeleteModalOpen(false);
          setMemberToDelete(null);
        }}
        size="medium"
        confirmButtonProps={{
          style: { backgroundColor: '#EF4444', borderColor: '#EF4444' },
          disabled: isDeletingInProgress,
        }}
      />
    </div>
  );
};

const mapStateToProps = ({ equip, rolesReduceer }) => ({
  projectTeamMembersList: get(equip, 'projectTeamMembersList', []),
  isProjectTeamMembersLoading: get(equip, 'isProjectTeamMembersLoading', false),
  isAddingMemberInProgress: get(equip, 'isAddingMemberInProgress', false),
  isDeletingInProgress: get(equip, 'isDeletingInProgress', false),
  roles: get(rolesReduceer, 'roles', []),
  nonCognitoUsersList: get(equip, 'nonCognitoUsersList', []),
  isAddingNonCognitoUser: get(equip, 'isAddingNonCognitoUser', false),
  projectTeamMembersAccessList: get(equip, 'projectTeamMembersAccessList', []),
  userAccessList: get(equip, 'userAccessList', []),
});

const mapDispatchToProps = {
  onFetchTeamMembersForProject: fetchTeamMembersForProject,
  onDeleteTeamMember: deleteTeamMember,
  onAddTeamMember: addTeamMember,
  onFetchRoles: fetchRoles,
  onAddRole: addRole,
  onGetAllNonCognitoUsers: getAllNonCognitoUsers,
  onAddNonCognitoUser: addNonCognitoUser,
};


export default connect(mapStateToProps, mapDispatchToProps)(DPRSettingsTeamsTab);
